import { Suspense, lazy, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import { Layout } from '@/components/shared/Layout';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import { ROUTES } from '@/constants/routes';
import './index.css';

const LoginPage = lazy(() => import('@/pages/LoginPage').then((module) => ({ default: module.LoginPage })));
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage }))
);
const LocationsPage = lazy(() =>
  import('@/pages/LocationsPage').then((module) => ({ default: module.LocationsPage }))
);
const LocationDetailPage = lazy(() =>
  import('@/pages/LocationDetailPage').then((module) => ({ default: module.LocationDetailPage }))
);
const LocationFormPage = lazy(() =>
  import('@/pages/LocationFormPage').then((module) => ({ default: module.LocationFormPage }))
);
const EquipmentPage = lazy(() =>
  import('@/pages/EquipmentPage').then((module) => ({ default: module.EquipmentPage }))
);
const ShipmentsPage = lazy(() =>
  import('@/pages/ShipmentsPage').then((module) => ({ default: module.ShipmentsPage }))
);
const ShipmentFormPage = lazy(() =>
  import('@/pages/ShipmentFormPage').then((module) => ({ default: module.ShipmentFormPage }))
);
const ShipmentDetailPage = lazy(() =>
  import('@/pages/ShipmentDetailPage').then((module) => ({ default: module.ShipmentDetailPage }))
);
const PackingSlipDetailPage = lazy(() =>
  import('@/pages/PackingSlipDetailPage').then((module) => ({ default: module.PackingSlipDetailPage }))
);
const PurchaseOrdersPage = lazy(() =>
  import('@/pages/PurchaseOrdersPage').then((module) => ({ default: module.PurchaseOrdersPage }))
);
const PurchaseOrderFormPage = lazy(() =>
  import('@/pages/PurchaseOrderFormPage').then((module) => ({ default: module.PurchaseOrderFormPage }))
);
const PurchaseOrderDetailPage = lazy(() =>
  import('@/pages/PurchaseOrderDetailPage').then((module) => ({ default: module.PurchaseOrderDetailPage }))
);
const InventoryCatalogPage = lazy(() =>
  import('@/pages/InventoryCatalogPage').then((module) => ({ default: module.InventoryCatalogPage }))
);
const ReserveInventoryPage = lazy(() =>
  import('@/pages/ReserveInventoryPage').then((module) => ({ default: module.ReserveInventoryPage }))
);
const SerialNumberSearchPage = lazy(() =>
  import('@/pages/SerialNumberSearchPage').then((module) => ({ default: module.SerialNumberSearchPage }))
);
const InventoryManagementPage = lazy(() =>
  import('@/pages/InventoryManagementPage').then((module) => ({ default: module.InventoryManagementPage }))
);
const UpcomingLaunchesPage = lazy(() =>
  import('@/pages/UpcomingLaunchesPage').then((module) => ({ default: module.UpcomingLaunchesPage }))
);
const ProductsPricingPage = lazy(() =>
  import('@/pages/ProductsPricingPage').then((module) => ({ default: module.ProductsPricingPage }))
);
const ContactsPage = lazy(() =>
  import('@/pages/ContactsPage').then((module) => ({ default: module.ContactsPage }))
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage }))
);
const ReportingPage = lazy(() =>
  import('@/pages/ReportingPage').then((module) => ({ default: module.ReportingPage }))
);
const EmailInsightsPage = lazy(() =>
  import('@/pages/EmailInsightsPage').then((module) => ({ default: module.EmailInsightsPage }))
);
const V2StatusPage = lazy(() =>
  import('@/pages/V2StatusPage').then((module) => ({ default: module.V2StatusPage }))
);
const V2StatusSharePage = lazy(() =>
  import('@/pages/V2StatusPage').then((module) => ({ default: module.V2StatusSharePage }))
);
const DealsPage = lazy(() => import('@/pages/DealsPage').then((module) => ({ default: module.DealsPage })));
const DemoFollowUpPacketPage = lazy(() =>
  import('@/pages/DemoFollowUpPacketPage').then((module) => ({ default: module.DemoFollowUpPacketPage }))
);
const LeadsPage = lazy(() => import('@/pages/LeadsPage').then((module) => ({ default: module.LeadsPage })));
const BlueprintLeadDetailPage = lazy(() =>
  import('@/pages/BlueprintLeadDetailPage').then((module) => ({ default: module.BlueprintLeadDetailPage }))
);
const SalesBillingPage = lazy(() =>
  import('@/pages/SalesBillingPage').then((module) => ({ default: module.SalesBillingPage }))
);
const QuoteFormPage = lazy(() =>
  import('@/pages/QuoteFormPage').then((module) => ({ default: module.QuoteFormPage }))
);
const InvoiceFormPage = lazy(() =>
  import('@/pages/InvoiceFormPage').then((module) => ({ default: module.InvoiceFormPage }))
);
const CashflowPage = lazy(() =>
  import('@/pages/CashflowPage').then((module) => ({ default: module.CashflowPage }))
);
const FinanceOverviewPage = lazy(() =>
  import('@/pages/FinanceOverviewPage').then((module) => ({ default: module.FinanceOverviewPage }))
);
const SlaAgreementsPage = lazy(() =>
  import('@/pages/SlaAgreementsPage').then((module) => ({ default: module.SlaAgreementsPage }))
);
const SlaAgreementDetailPage = lazy(() =>
  import('@/pages/SlaAgreementDetailPage').then((module) => ({ default: module.SlaAgreementDetailPage }))
);
const SlaAgreementSigningPage = lazy(() =>
  import('@/pages/SlaAgreementSigningPage').then((module) => ({ default: module.SlaAgreementSigningPage }))
);
const CustomerOpsHubPage = lazy(() =>
  import('@/pages/CustomerOpsHubPage').then((module) => ({ default: module.CustomerOpsHubPage }))
);
const CustomerOpsProofPage = lazy(() =>
  import('@/pages/CustomerOpsProofPage').then((module) => ({ default: module.CustomerOpsProofPage }))
);
const CustomerOpsProofNewRedirect = lazy(() =>
  import('@/pages/CustomerOpsProofPage').then((module) => ({ default: module.CustomerOpsProofNewRedirect }))
);
const CustomerOpsProofReviewRedirect = lazy(() =>
  import('@/pages/CustomerOpsProofPage').then((module) => ({ default: module.CustomerOpsProofReviewRedirect }))
);
const CustomerOpsProofDetailRedirect = lazy(() =>
  import('@/pages/CustomerOpsProofPage').then((module) => ({ default: module.CustomerOpsProofDetailRedirect }))
);
const CustomerOpsReviewDuePage = lazy(() =>
  import('@/pages/CustomerOpsReviewDuePage').then((module) => ({ default: module.CustomerOpsReviewDuePage }))
);
const CustomerOpsReviewDueDetailPage = lazy(() =>
  import('@/pages/CustomerOpsReviewDueDetailPage').then((module) => ({
    default: module.CustomerOpsReviewDueDetailPage,
  }))
);
const CustomerOpsAtRiskPage = lazy(() =>
  import('@/pages/CustomerOpsAtRiskPage').then((module) => ({ default: module.CustomerOpsAtRiskPage }))
);
const CustomerOpsAtRiskDetailPage = lazy(() =>
  import('@/pages/CustomerOpsAtRiskDetailPage').then((module) => ({
    default: module.CustomerOpsAtRiskDetailPage,
  }))
);
const CustomerOpsContentPage = lazy(() =>
  import('@/pages/CustomerOpsContentPage').then((module) => ({ default: module.CustomerOpsContentPage }))
);
const CustomerOpsContentDetailPage = lazy(() =>
  import('@/pages/CustomerOpsContentDetailPage').then((module) => ({
    default: module.CustomerOpsContentDetailPage,
  }))
);
const CustomerOpsPlacementAuditsPage = lazy(() =>
  import('@/pages/CustomerOpsPlacementAuditsPage').then((module) => ({
    default: module.CustomerOpsPlacementAuditsPage,
  }))
);
const CustomerOpsPlacementAuditDetailPage = lazy(() =>
  import('@/pages/CustomerOpsPlacementAuditDetailPage').then((module) => ({
    default: module.CustomerOpsPlacementAuditDetailPage,
  }))
);
const CustomerOpsUserAccessReviewPage = lazy(() =>
  import('@/pages/CustomerOpsUserAccessReviewPage').then((module) => ({
    default: module.CustomerOpsUserAccessReviewPage,
  }))
);
const NetworkPulsePage = lazy(() =>
  import('@/pages/NetworkPulsePage').then((module) => ({
    default: module.NetworkPulsePage,
  }))
);
const NetworkPulseProgramReportPage = lazy(() =>
  import('@/pages/NetworkPulseProgramReportPage').then((module) => ({
    default: module.NetworkPulseProgramReportPage,
  }))
);
const NetworkPulseCampaignReportPage = lazy(() =>
  import('@/pages/NetworkPulseCampaignReportPage').then((module) => ({
    default: module.NetworkPulseCampaignReportPage,
  }))
);
const StorePulseAlertsPage = lazy(() =>
  import('@/pages/StorePulseAlertsPage').then((module) => ({
    default: module.StorePulseAlertsPage,
  }))
);
const CustomerOpsUserAccessReviewsPage = lazy(() =>
  import('@/pages/CustomerOpsUserAccessReviewsPage').then((module) => ({
    default: module.CustomerOpsUserAccessReviewsPage,
  }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  },
});

function RouteFallback() {
  return (
    <div className="flex min-h-[320px] items-center justify-center">
      <div className="text-sm text-gray-600">Loading page...</div>
    </div>
  );
}

function withSuspense(node: ReactNode) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<RouteFallback />}>{node}</Suspense>
    </RouteErrorBoundary>
  );
}

function protectedPage(node: ReactNode) {
  return (
    <ProtectedRoute>
      <Layout>{withSuspense(node)}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path={ROUTES.LOGIN} element={withSuspense(<LoginPage />)} />
            <Route
              path={ROUTES.SLA_AGREEMENT_SIGN(':token')}
              element={withSuspense(<SlaAgreementSigningPage />)}
            />
            <Route path={ROUTES.DASHBOARD} element={protectedPage(<DashboardPage />)} />
            <Route path={ROUTES.LOCATIONS} element={protectedPage(<LocationsPage />)} />
            <Route path={`${ROUTES.LOCATIONS}/new`} element={protectedPage(<LocationFormPage />)} />
            <Route path={`${ROUTES.LOCATIONS}/:id/edit`} element={protectedPage(<LocationFormPage />)} />
            <Route path={`${ROUTES.LOCATIONS}/:id`} element={protectedPage(<LocationDetailPage />)} />
            <Route path={ROUTES.EQUIPMENT} element={protectedPage(<EquipmentPage />)} />
            <Route
              path={ROUTES.INVENTORY_CATALOG}
              element={protectedPage(<InventoryCatalogPage warehouse="Estone" />)}
            />
            <Route path={ROUTES.INVENTORY_RESERVES} element={protectedPage(<ReserveInventoryPage />)} />
            <Route
              path={ROUTES.INVENTORY_SERIAL_SEARCH}
              element={protectedPage(<SerialNumberSearchPage warehouse="Estone" />)}
            />
            <Route path={ROUTES.UPCOMING_ORDERS} element={<Navigate to={ROUTES.UPCOMING_LAUNCHES} replace />} />
            <Route path={ROUTES.UPCOMING_LAUNCHES} element={protectedPage(<UpcomingLaunchesPage />)} />
            <Route path={ROUTES.PRODUCTS_PRICING} element={protectedPage(<ProductsPricingPage />)} />
            <Route path={ROUTES.REPORTING} element={protectedPage(<ReportingPage />)} />
            <Route path={ROUTES.EMAIL_INSIGHTS} element={protectedPage(<EmailInsightsPage />)} />
            <Route path={ROUTES.NETWORK_PULSE_REPORTS} element={protectedPage(<NetworkPulseProgramReportPage />)} />
            <Route path={ROUTES.NETWORK_PULSE_CAMPAIGN_REPORT_PATTERN} element={protectedPage(<NetworkPulseCampaignReportPage />)} />
            <Route path={ROUTES.NETWORK_PULSE} element={protectedPage(<NetworkPulsePage />)} />
            <Route path={ROUTES.STORE_PULSE_ALERTS} element={protectedPage(<StorePulseAlertsPage />)} />
            {/* Deprecated: Network Pulse moved out of Customer Ops onto its own top-level page. */}
            <Route
              path={ROUTES.CUSTOMER_OPS_NETWORK_PULSE}
              element={<Navigate to={ROUTES.NETWORK_PULSE} replace />}
            />
            {/* Public read-only view: shareable URL with no admin shell and no auth requirement. */}
            <Route path={ROUTES.V2_STATUS_SHARE} element={withSuspense(<V2StatusSharePage />)} />
            <Route path={ROUTES.V2_STATUS} element={protectedPage(<V2StatusPage />)} />
            <Route path={ROUTES.INVENTORY_MANAGEMENT} element={protectedPage(<InventoryManagementPage />)} />
            <Route
              path={ROUTES.INVENTORY_CATALOG}
              element={<Navigate to={{ pathname: ROUTES.INVENTORY_MANAGEMENT, search: '?tab=estone' }} replace />}
            />
            <Route
              path={ROUTES.INVENTORY_RESERVES}
              element={<Navigate to={{ pathname: ROUTES.INVENTORY_MANAGEMENT, search: '?tab=estone' }} replace />}
            />
            <Route
              path={ROUTES.INVENTORY_SERIAL_SEARCH}
              element={<Navigate to={{ pathname: ROUTES.INVENTORY_MANAGEMENT, search: '?tab=estone' }} replace />}
            />
            <Route path={ROUTES.SHIPMENTS} element={protectedPage(<ShipmentsPage />)} />
            <Route path={`${ROUTES.SHIPMENTS}/new`} element={protectedPage(<ShipmentFormPage />)} />
            <Route path={`${ROUTES.SHIPMENTS}/:id/edit`} element={protectedPage(<ShipmentFormPage />)} />
            <Route path={`${ROUTES.SHIPMENTS}/:id`} element={protectedPage(<ShipmentDetailPage />)} />
            <Route path="/packing-slips/:id" element={protectedPage(<PackingSlipDetailPage />)} />
            <Route path={ROUTES.PURCHASE_ORDERS} element={protectedPage(<PurchaseOrdersPage />)} />
            <Route path={`${ROUTES.PURCHASE_ORDERS}/new`} element={protectedPage(<PurchaseOrderFormPage />)} />
            <Route
              path={`${ROUTES.PURCHASE_ORDERS}/:id/edit`}
              element={protectedPage(<PurchaseOrderFormPage />)}
            />
            <Route path={`${ROUTES.PURCHASE_ORDERS}/:id`} element={protectedPage(<PurchaseOrderDetailPage />)} />
            <Route path={ROUTES.CONTACTS} element={protectedPage(<ContactsPage />)} />
            <Route path={ROUTES.SETTINGS} element={protectedPage(<SettingsPage />)} />
            <Route path={ROUTES.DEMO_FOLLOW_UP_PACKET} element={protectedPage(<DemoFollowUpPacketPage />)} />
            <Route path={ROUTES.DEALS} element={protectedPage(<DealsPage />)} />
            <Route path={ROUTES.LEADS} element={protectedPage(<LeadsPage />)} />
            <Route
              path={ROUTES.BLUEPRINT_LEADS}
              element={<Navigate to={ROUTES.LEADS_BLUEPRINT_TAB} replace />}
            />
            <Route
              path={ROUTES.BLUEPRINT_LEAD_DETAIL(':id')}
              element={protectedPage(<BlueprintLeadDetailPage />)}
            />
            <Route path={ROUTES.SALES_BILLING} element={protectedPage(<SalesBillingPage />)} />
            <Route path={ROUTES.SLA_AGREEMENTS} element={protectedPage(<SlaAgreementsPage />)} />
            <Route
              path={ROUTES.SLA_AGREEMENT_DETAIL(':id')}
              element={protectedPage(<SlaAgreementDetailPage />)}
            />
            <Route path={ROUTES.CASHFLOW} element={protectedPage(<FinanceOverviewPage />)} />
            <Route path={ROUTES.CASHFLOW_STATEMENT} element={protectedPage(<CashflowPage />)} />
            <Route path={ROUTES.CUSTOMER_OPS} element={protectedPage(<CustomerOpsHubPage />)} />
            <Route
              path={`${ROUTES.CUSTOMER_OPS_PROOF}/prototype`}
              element={<Navigate to={ROUTES.CUSTOMER_OPS_PROOF} replace />}
            />
            <Route path={ROUTES.CUSTOMER_OPS_PROOF_NEW} element={<CustomerOpsProofNewRedirect />} />
            <Route path={ROUTES.CUSTOMER_OPS_PROOF_REVIEW(':runId')} element={<CustomerOpsProofReviewRedirect />} />
            <Route path={ROUTES.CUSTOMER_OPS_PROOF_DETAIL(':proofMetricId')} element={<CustomerOpsProofDetailRedirect />} />
            <Route path={ROUTES.CUSTOMER_OPS_PROOF} element={protectedPage(<CustomerOpsProofPage />)} />
            <Route path={ROUTES.CUSTOMER_OPS_REVIEW_DUE} element={protectedPage(<CustomerOpsReviewDuePage />)} />
            <Route
              path={ROUTES.CUSTOMER_OPS_REVIEW_DUE_DETAIL(':id')}
              element={protectedPage(<CustomerOpsReviewDueDetailPage />)}
            />
            <Route path={ROUTES.CUSTOMER_OPS_AT_RISK} element={protectedPage(<CustomerOpsAtRiskPage />)} />
            <Route
              path={ROUTES.CUSTOMER_OPS_AT_RISK_DETAIL(':id')}
              element={protectedPage(<CustomerOpsAtRiskDetailPage />)}
            />
            <Route path={ROUTES.CUSTOMER_OPS_CONTENT} element={protectedPage(<CustomerOpsContentPage />)} />
            <Route
              path={ROUTES.CUSTOMER_OPS_CONTENT_DETAIL(':id')}
              element={protectedPage(<CustomerOpsContentDetailPage />)}
            />
            <Route
              path={ROUTES.CUSTOMER_OPS_PLACEMENT_AUDITS}
              element={protectedPage(<CustomerOpsPlacementAuditsPage />)}
            />
            <Route
              path={ROUTES.CUSTOMER_OPS_PLACEMENT_AUDIT_DETAIL(':id')}
              element={protectedPage(<CustomerOpsPlacementAuditDetailPage />)}
            />
            {/* Public, token-scoped survey. Keep this outside ProtectedRoute and the admin Layout. */}
            <Route
              path={ROUTES.CUSTOMER_OPS_USER_ACCESS_REVIEW_DETAIL(':token')}
              element={withSuspense(<CustomerOpsUserAccessReviewPage />)}
            />
            {/* Internal queue. Authentication is required before the admin API can be called. */}
            <Route
              path={ROUTES.CUSTOMER_OPS_USER_ACCESS_REVIEWS}
              element={protectedPage(<CustomerOpsUserAccessReviewsPage />)}
            />
            <Route path={`${ROUTES.SALES_BILLING}/quotes/new`} element={protectedPage(<QuoteFormPage />)} />
            <Route
              path={`${ROUTES.SALES_BILLING}/quotes/:id/edit`}
              element={protectedPage(<QuoteFormPage />)}
            />
            <Route path={`${ROUTES.SALES_BILLING}/invoices/new`} element={protectedPage(<InvoiceFormPage />)} />
            <Route
              path={`${ROUTES.SALES_BILLING}/invoices/:id/edit`}
              element={protectedPage(<InvoiceFormPage />)}
            />
            <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            <Route
              path="*"
              element={
                <div className="flex min-h-screen items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold">404</h1>
                    <p className="text-gray-600">Page not found</p>
                  </div>
                </div>
              }
            />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
